import { configureStore, createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import posthogLib from 'posthog-js';
import { combineReducers } from 'redux';

import dashboardEditReducer from '@/features/Dashboard/pages/Edit/model/dashboardEdit.slice';
import authReducer from '@/slices/authSlice';
import dhDataReducer from '@/slices/dhDataSlice';
import marketsListReducer from '@/slices/marketsListSlice';
import tripTokenReducer from '@/slices/tripTokenSlice';

import listApiKeyReducer from './slices/apiKeyListSlice';
import dataHubReducer from './slices/dataHubSlice';
import modalReducer from './slices/modalSlice';
import symbolSelectorReducer, {
  addSymbol,
  addSymbolArray,
  setSymbols,
  toggleSymbol
} from './slices/symbolSelectorSlice';
import userConfigReducer from './slices/userConfigSlice';
import userCreationReducer from './slices/userCreationSlice';

export const rootReducer = combineReducers({
  auth: authReducer,
  tripToken: tripTokenReducer,
  symbolSelector: symbolSelectorReducer,
  dhData: dhDataReducer,
  marketsList: marketsListReducer,
  userConfig: userConfigReducer,
  dataHub: dataHubReducer,
  listApiKey: listApiKeyReducer,
  userCreation: userCreationReducer,
  modal: modalReducer,
  dashboardEdit: dashboardEditReducer
});
// Listener middleware to keep reducers pure and track selection events
const selectionListener = createListenerMiddleware();

const symbolKey = (
  symbol: { Code: string; Period: string | number; PeriodType: string; TimeRef: string | number },
  isForwardMode = false
) =>
  isForwardMode
    ? `${symbol.Code}-${symbol.PeriodType}-${symbol.TimeRef}`
    : `${symbol.Code}-${symbol.PeriodType}-${symbol.Period}-${symbol.TimeRef}`;

selectionListener.startListening({
  matcher: isAnyOf(addSymbol, addSymbolArray, setSymbols, toggleSymbol),
  effect: (action, api) => {
    const prev = (api.getOriginalState() as any).symbolSelector;
    const next = (api.getState() as any).symbolSelector;
    const isForwardMode = next.isForwardMode;

    const prevKeys = new Set<string>(prev.symbols.map((s: any) => symbolKey(s, isForwardMode)));
    const added = (next.symbols as any[]).filter(s => !prevKeys.has(symbolKey(s, isForwardMode)));
    if (added.length) {
      // Defer analytics to avoid blocking the UI thread during interactions
      const schedule = (fn: () => void) => {
        if (typeof globalThis !== 'undefined' && 'requestIdleCallback' in globalThis) {
          (globalThis as any).requestIdleCallback(() => {
            fn();
          });
        } else {
          setTimeout(fn, 0);
        }
      };
      schedule(() => {
        for (const symbol of added) {
          try {
            posthogLib.capture('symbol_select', { symbol });
          } catch {
            // non-fatal
          }
        }
      });
    }
  }
});

export const store = configureStore({
  // process.env.NODE_ENV === 'development'
  devTools: import.meta.env.DEV,
  middleware: getDefaultMiddleware => getDefaultMiddleware().prepend(selectionListener.middleware),
  reducer: rootReducer
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;

setupListeners(store.dispatch);
